import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { FileText, ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/support"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Support Hub
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
                  Terms of Use
                </h1>
                <p className="text-muted-foreground">Last updated: December 2024</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-white/10 rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none space-y-8">

                {/* Table of Contents */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">Table of Contents</h2>
                  <ol className="list-decimal pl-6 space-y-1 text-muted-foreground text-sm">
                    <li>Agreement to Terms</li>
                    <li>Intellectual Property Rights</li>
                    <li>User Representations</li>
                    <li>User Registration</li>
                    <li>Prohibited Activities</li>
                    <li>User Generated Contributions</li>
                    <li>Contribution License</li>
                    <li>Guidelines for Reviews</li>
                    <li>Mobile Application License</li>
                    <li>Social Media</li>
                    <li>Submissions</li>
                    <li>Third-Party Web App and Content</li>
                    <li>Advertisers</li>
                    <li>App Management</li>
                    <li>Copyright Infringements</li>
                    <li>Term and Termination</li>
                    <li>Modifications and Interruptions</li>
                    <li>Governing Law</li>
                    <li>Dispute Resolution</li>
                    <li>Corrections</li>
                    <li>Disclaimer</li>
                    <li>Limitations of Liability</li>
                    <li>Indemnification</li>
                    <li>User Data</li>
                    <li>Electronic Communications, Transactions, and Signatures</li>
                    <li>California Users and Residents</li>
                    <li>Miscellaneous</li>
                    <li>Contact Us</li>
                  </ol>
                </div>

                {/* Section 1 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms of Use constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Playmatez, doing business as Playmatez ("Playmatez," "we," "us," or "our"), concerning your access to and use of the https://www.playmatez.io web, Playmatez App (hosted in both Google Play Store as well as Apple Store) as well as any other media form, media channel, mobile webApp or mobile application related, linked, or otherwise connected thereto (collectively, the "App").
                  </p>
                  <div className="p-4 bg-primary/10 border border-primary/20 rounded-xl mb-4">
                    <p className="text-white font-medium">IMPORTANT:</p>
                    <p className="text-muted-foreground text-sm mt-1">
                      You agree that by accessing the App, you have read, understood, and agreed to be bound by all of these Terms of Use. IF YOU DO NOT AGREE WITH ALL OF THESE TERMS OF USE, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE APP AND YOU MUST DISCONTINUE USE IMMEDIATELY.
                    </p>
                  </div>
                  <p className="text-muted-foreground">
                    We reserve the right, in our sole discretion, to make changes or modifications to these Terms of Use from time to time. We will alert you about any changes by updating the "Last updated" date of these Terms of Use.
                  </p>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">2. Intellectual Property Rights</h2>
                  <p className="text-muted-foreground mb-4">
                    Unless otherwise indicated, the App is our proprietary property and all source code, databases, functionality, software, webApp designs, audio, video, text, photographs, and graphics on the App (collectively, the "Content") and the trademarks, service marks, and logos contained therein (the "Marks") are owned or controlled by us or licensed to us, and are protected by copyright and trademark laws.
                  </p>
                  <p className="text-muted-foreground">
                    The Content and the Marks are provided on the App "AS IS" for your information and personal use only. Except as expressly provided in these Terms of Use, no part of the App and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission.
                  </p>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">3. User Representations</h2>
                  <p className="text-muted-foreground mb-4">By using the App, you represent and warrant that:</p>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>All registration information you submit will be true, accurate, current, and complete;</li>
                    <li>You will maintain the accuracy of such information and promptly update such registration information as necessary;</li>
                    <li>You have the legal capacity, and you agree to comply with these Terms of Use;</li>
                    <li>You are not a minor in the jurisdiction in which you reside, or if a minor, you have received parental permission to use the App;</li>
                    <li>You will not access the App through automated or non-human means, whether through a bot, script, or otherwise;</li>
                    <li>You will not use the App for any illegal or unauthorized purpose;</li>
                    <li>Your use of the App will not violate any applicable law or regulation.</li>
                  </ol>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">4. User Registration</h2>
                  <p className="text-muted-foreground">
                    You may be required to register with the App. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
                  </p>
                  <p className="text-muted-foreground mt-4">
                    Also, please find in detail the user types and their possibilities at <a href="https://playmatez.io/support-hub" className="text-primary hover:underline">https://playmatez.io/support-hub</a>
                  </p>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">5. Prohibited Activities</h2>
                  <p className="text-muted-foreground mb-4">
                    You may not access or use the App for any purpose other than that for which we make the App available. The App may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
                  </p>
                  <p className="text-muted-foreground mb-4">As a user of the App, you agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm">
                    <li>Systematically retrieve data or other content from the App to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.</li>
                    <li>Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.</li>
                    <li>Circumvent, disable, or otherwise interfere with security-related features of the App.</li>
                    <li>Disparage, tarnish, or otherwise harm, in our opinion, us and/or the App.</li>
                    <li>Use any information obtained from the App in order to harass, abuse, or harm another person.</li>
                    <li>Make improper use of our support services or submit false reports of abuse or misconduct.</li>
                    <li>Use the App in a manner inconsistent with any applicable laws or regulations.</li>
                    <li>Engage in unauthorized framing of or linking to the App.</li>
                    <li>Upload or transmit viruses, Trojan horses, or other material that interferes with any party's uninterrupted use and enjoyment of the App.</li>
                    <li>Engage in any automated use of the system, such as using scripts to send comments or messages.</li>
                    <li>Delete the copyright or other proprietary rights notice from any Content.</li>
                    <li>Attempt to impersonate another user or person.</li>
                    <li>Interfere with, disrupt, or create an undue burden on the App or the networks or services connected to the App.</li>
                    <li>Harass, annoy, intimidate, or threaten any of our employees or agents.</li>
                    <li>Attempt to bypass any measures of the App designed to prevent or restrict access.</li>
                    <li>Copy or adapt the App's software.</li>
                    <li>Use the App to advertise or offer to sell goods and services that are prohibited.</li>
                  </ul>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">6. User Generated Contributions</h2>
                  <p className="text-muted-foreground mb-4">
                    The App may invite you to chat, contribute to, or participate in blogs, message boards, online forums, and other functionality, and may provide you with the opportunity to create, submit, post, display, transmit, perform, publish, distribute, or broadcast content and materials to us or on the App.
                  </p>
                  <p className="text-muted-foreground mb-4">When you create or make available any Contributions, you thereby represent and warrant that:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm">
                    <li>The creation, distribution, transmission, public display, or performance of your Contributions do not infringe the proprietary rights of any third party.</li>
                    <li>You are the creator and owner of or have the necessary licenses, rights, consents, releases, and permissions to use your Contributions.</li>
                    <li>Your Contributions are not false, inaccurate, or misleading.</li>
                    <li>Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, or other forms of solicitation.</li>
                    <li>Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libelous, slanderous, or otherwise objectionable.</li>
                    <li>Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone.</li>
                    <li>Your Contributions do not violate any applicable law, regulation, or rule.</li>
                    <li>Your Contributions do not violate the privacy or publicity rights of any third party.</li>
                  </ul>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">7. Contribution License</h2>
                  <p className="text-muted-foreground">
                    By posting your Contributions to any part of the App, you automatically grant, and you represent and warrant that you have the right to grant, to us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and license to host, use, copy, reproduce, disclose, sell, resell, publish, broadcast, retitle, archive, store, cache, publicly perform, publicly display, reformat, translate, transmit, excerpt (in whole or in part), and distribute such Contributions for any purpose.
                  </p>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">8. Guidelines for Reviews</h2>
                  <p className="text-muted-foreground mb-4">We may provide you areas on the App to leave reviews or ratings. When posting a review, you must comply with the following criteria:</p>
                  <ol className="list-decimal pl-6 space-y-1 text-muted-foreground text-sm">
                    <li>You should have first hand experience with the person/entity being reviewed;</li>
                    <li>Your reviews should not contain offensive profanity, or abusive, racist, offensive, or hate language;</li>
                    <li>Your reviews should not contain discriminatory references based on religion, race, gender, national origin, age, marital status, sexual orientation, or disability;</li>
                    <li>Your reviews should not contain references to illegal activity;</li>
                    <li>You should not be affiliated with competitors if posting negative reviews;</li>
                    <li>You should not make any conclusions as to the legality of conduct;</li>
                    <li>You may not post any false or misleading statements;</li>
                    <li>You may not organize a campaign encouraging others to post reviews, whether positive or negative.</li>
                  </ol>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">9. Mobile Application License</h2>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">Use License</h3>
                  <p className="text-muted-foreground mb-4">
                    If you access the App via a mobile application, then we grant you a revocable, non-exclusive, non-transferable, limited right to install and use the mobile application on wireless electronic devices owned or controlled by you, and to access and use the mobile application on such devices strictly in accordance with the terms and conditions of this mobile application license.
                  </p>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">Apple and Android Devices</h3>
                  <p className="text-muted-foreground">
                    The following terms apply when you use a mobile application obtained from either the Apple Store or Google Play (each an "App Distributor") to access the App. The license granted to you for our mobile application is limited to a non-transferable license to use the application on a device that utilizes the Apple iOS or Android operating systems.
                  </p>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">10. Social Media</h2>
                  <p className="text-muted-foreground">
                    As part of the functionality of the App, you may link your account with online accounts you have with third-party service providers (each such account, a "Third-Party Account") by either: (1) providing your Third-Party Account login information through the App; or (2) allowing us to access your Third-Party Account, as is permitted under the applicable terms and conditions that govern your use of each Third-Party Account.
                  </p>
                </div>

                {/* Section 11 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">11. Submissions</h2>
                  <p className="text-muted-foreground">
                    You acknowledge and agree that any questions, comments, suggestions, ideas, feedback, or other information regarding the App ("Submissions") provided by you to us are non-confidential and shall become our sole property. We shall own exclusive rights, including all intellectual property rights, and shall be entitled to the unrestricted use and dissemination of these Submissions for any lawful purpose.
                  </p>
                </div>

                {/* Section 12 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">12. Third-Party Web App and Content</h2>
                  <p className="text-muted-foreground">
                    The App may contain links to other webApps ("Third-Party WebApps") as well as articles, photographs, text, graphics, pictures, designs, music, sound, video, information, applications, software, and other content or items belonging to or originating from third parties ("Third-Party Content"). Such Third-Party WebApps and Third-Party Content are not investigated, monitored, or checked for accuracy, appropriateness, or completeness by us, and we are not responsible for any Third-Party WebApps accessed through the App.
                  </p>
                </div>

                {/* Section 13 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">13. Advertisers</h2>
                  <p className="text-muted-foreground">
                    We allow advertisers to display their advertisements and other information in certain areas of the App. If you are an advertiser, you shall take full responsibility for any advertisements you place on the App and any services provided on the App or products sold through those advertisements.
                  </p>
                </div>

                {/* Section 14 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">14. App Management</h2>
                  <p className="text-muted-foreground">
                    We reserve the right, but not the obligation, to: (1) monitor the App for violations of these Terms of Use; (2) take appropriate legal action against anyone who, in our sole discretion, violates the law or these Terms of Use; (3) in our sole discretion and without limitation, refuse, restrict access to, limit the availability of, or disable any of your Contributions; (4) in our sole discretion and without limitation, remove from the App or otherwise disable all files and content that are excessive in size or are in any way burdensome to our systems; and (5) otherwise manage the App in a manner designed to protect our rights and property.
                  </p>
                </div>

                {/* Section 15 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">15. Copyright Infringements</h2>
                  <p className="text-muted-foreground">
                    We respect the intellectual property rights of others. If you believe that any material available on or through the App infringes upon any copyright you own or control, please immediately notify us using the contact information provided below.
                  </p>
                </div>

                {/* Section 16 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">16. Term and Termination</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms of Use shall remain in full force and effect while you use the App. WITHOUT LIMITING ANY OTHER PROVISION OF THESE TERMS OF USE, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE APP TO ANY PERSON FOR ANY REASON OR FOR NO REASON.
                  </p>
                  <p className="text-muted-foreground">
                    If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party.
                  </p>
                </div>

                {/* Section 17 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">17. Modifications and Interruptions</h2>
                  <p className="text-muted-foreground">
                    We reserve the right to change, modify, or remove the contents of the App at any time or for any reason at our sole discretion without notice. We also reserve the right to modify or discontinue all or part of the App without notice at any time. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the App.
                  </p>
                </div>

                {/* Section 18 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">18. Governing Law</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms shall be governed by and interpreted according to the laws of the jurisdiction in which you primarily access or use the Playmatez Services:
                  </p>
                  <div className="space-y-3">
                    {[
                      { country: "Australia", law: "Laws of New South Wales, Australia. Courts of New South Wales." },
                      { country: "India", law: "Laws of India. Courts located in Hyderabad, Telangana." },
                      { country: "United States", law: "Laws of the State of Delaware, USA. Courts of Delaware." },
                      { country: "Canada", law: "Laws of the Province of Ontario, Canada. Courts of Toronto, Ontario." },
                      { country: "Singapore", law: "Laws of Singapore. Courts of Singapore." },
                      { country: "New Zealand", law: "Laws of New Zealand. Courts of Auckland, New Zealand." },
                    ].map((item, i) => (
                      <div key={i} className="p-3 bg-white/5 rounded-lg">
                        <p className="text-white font-medium text-sm">{item.country}</p>
                        <p className="text-muted-foreground text-xs mt-1">{item.law}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 19 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">19. Dispute Resolution</h2>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">Binding Arbitration</h3>
                  <p className="text-muted-foreground mb-4">
                    Any dispute arising out of or in connection with these Terms, including any question regarding its existence, validity, or termination, shall be referred to and finally resolved by arbitration.
                  </p>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">Restrictions</h3>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground text-sm">
                    <li>No arbitration shall be joined with another proceeding.</li>
                    <li>There shall be no right or authority for any dispute to be arbitrated on a class-action basis.</li>
                    <li>There shall be no right or authority for any dispute to be brought in a representative capacity on behalf of others.</li>
                  </ul>
                </div>

                {/* Section 20 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">20. Corrections</h2>
                  <p className="text-muted-foreground">
                    There may be information on the App that contains typographical errors, inaccuracies, or omissions. We reserve the right to correct any errors, inaccuracies, permissions and to change or update the information on the App at any time, without prior notice.
                  </p>
                </div>

                {/* Section 21 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">21. Disclaimer</h2>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                    <p className="text-muted-foreground text-sm">
                      THE APP IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE APP AND OUR SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE APP AND YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                    </p>
                  </div>
                </div>

                {/* Section 22 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">22. Limitations of Liability</h2>
                  <p className="text-muted-foreground">
                    IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE APP, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                </div>

                {/* Section 23 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">23. Indemnification</h2>
                  <p className="text-muted-foreground">
                    You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of: (1) your Contributions; (2) use of the App; (3) breach of these Terms of Use; (4) any breach of your representations and warranties set forth in these Terms of Use; (5) your violation of the rights of a third party; or (6) any overt harmful act toward any other user of the App.
                  </p>
                </div>

                {/* Section 24 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">24. User Data</h2>
                  <p className="text-muted-foreground">
                    We will maintain certain data that you transmit to the App for the purpose of managing the performance of the App, as well as data relating to your use of the App. Although we perform regular routine backups of data, you are solely responsible for all data that you transmit or that relates to any activity you have undertaken using the App.
                  </p>
                </div>

                {/* Section 25 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">25. Electronic Communications, Transactions, and Signatures</h2>
                  <p className="text-muted-foreground">
                    Visiting the App, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically, via email and on the App, satisfy any legal requirement that such communication be in writing.
                  </p>
                </div>

                {/* Section 26 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">26. California Users and Residents</h2>
                  <p className="text-muted-foreground">
                    If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at (800) 952-5210 or (916) 445-1254.
                  </p>
                </div>

                {/* Section 27 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">27. Miscellaneous</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms of Use and any policies or operating rules posted by us on the App or in respect to the App constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Terms of Use shall not operate as a waiver of such right or provision.
                  </p>
                  <p className="text-muted-foreground">
                    Further the following Terms of use for various features of our app are detailed in the website and form part of this document:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground text-sm mt-2">
                    <li>Playmatez Venue Usage Policy</li>
                    <li>Safety and Safe Usage Policy for Playmatez Marketplace</li>
                    <li>Managing Your Account</li>
                    <li>Event Hosting Policy</li>
                  </ul>
                </div>

                {/* Section 28 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">28. Contact Us</h2>
                  <p className="text-muted-foreground mb-4">
                    In order to resolve a complaint regarding the App or to receive further information regarding use of the App, please contact us at:
                  </p>
                  <div className="p-4 bg-primary/10 border border-primary/20 rounded-xl">
                    <p className="text-white font-medium">Playmatez</p>
                    <p className="text-primary mt-2">support@playmatez.com.au</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
